import { type Href, useRouter } from "expo-router";
import { Pressable, StyleSheet, View } from "react-native";

import type { DisplayEvent } from "@/utils/event-helpers";

import { DarkBlurCard } from "@/components/DarkBlurCard";
import { GText } from "@/components/g-text";
import { colors } from "@/constants/colors";
import { formatEventDate, formatRsvpStatus } from "@/utils/copy";

export function EventCard({
  event,
  showStatus,
}: {
  event: DisplayEvent;
  showStatus: boolean;
}) {
  const router = useRouter();

  return (
    <Pressable
      onPress={() => router.push(`/e/${event.slug}` as Href)}
      style={styles.card}
    >
      <DarkBlurCard imageUrl={event.flyer_url} style={styles.cardContent}>
        <View style={styles.topRow}>
          <GText style={styles.cardTitle}>
            {event.title || "Untitled Event"}
          </GText>
          <View style={styles.dateRow}>
            {event.startISO ? (
              <GText style={[styles.cardDate, styles.cardDateFlexGrow]}>
                {formatEventDate(event.startISO)}
              </GText>
            ) : null}

            {event.rsvpStatus && (
              <GText style={styles.cardDate}>
                {formatRsvpStatus(event.rsvpStatus)}
              </GText>
            )}
          </View>
          {event.venue ? (
            <GText style={styles.cardVenue}>{event.venue}</GText>
          ) : null}
        </View>
        {event.visibility !== "public" && (
          <View style={styles.inviteOnlyRow}>
            <View style={styles.statusBadge}>
              <GText style={styles.statusBadgeText}>invite only</GText>
            </View>
          </View>
        )}
        {showStatus && (
          <View style={styles.statusRow}>
            <View
              style={[
                styles.statusBadge,
                event.status === "cancelled" && styles.statusBadgeCancelled,
              ]}
            >
              <GText
                style={[
                  styles.statusBadgeText,
                  event.status === "cancelled" &&
                    styles.statusBadgeTextCancelled,
                ]}
              >
                {event.status}
              </GText>
            </View>
          </View>
        )}
      </DarkBlurCard>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  card: {
    borderRadius: 26,
    marginBottom: 16,
    overflow: "hidden" as const,
  },
  cardContent: {
    flexDirection: "column",
    minHeight: 160,
    padding: 20,
    position: "relative",
  },
  cardDate: {
    color: colors.white,
    fontSize: 14,
    marginBottom: 4,
  },
  cardDateFlexGrow: {
    flex: 1,
  },
  cardTitle: {
    color: colors.white,
    fontSize: 20,
    fontWeight: "700",
    marginBottom: 6,
  },
  cardVenue: {
    color: colors.white,
    fontSize: 14,
    marginBottom: 4,
    opacity: 0.8,
  },
  dateRow: {
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  inviteOnlyRow: {
    alignItems: "center",
    bottom: 16,
    left: 16,
    position: "absolute",
  },
  statusBadge: {
    backgroundColor: colors.white,
    borderRadius: 10,
    paddingHorizontal: 10,
    paddingVertical: 3,
  },
  statusBadgeCancelled: {
    backgroundColor: colors.red,
  },
  statusBadgeText: {
    color: colors.black,
    fontSize: 12,
    fontWeight: "700",
  },
  statusBadgeTextCancelled: {
    color: colors.white,
  },
  statusRow: {
    alignItems: "center",
    bottom: 16,

    position: "absolute",
    right: 16,
  },
  topRow: {
    alignItems: "flex-start",
    flex: 1,
    justifyContent: "center",
  },
});
